/**
 * Fetch diagnostic reports for a gateway, grouped by session_id.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { GatewayDiagnosticReport } from '@/types/gateway';

export interface DiagnosticSession {
  session_id: string;
  reports: GatewayDiagnosticReport[];
  start: string;
  end: string;
}

export function useGatewayDiagnostics(deviceId: string | undefined) {
  const query = useQuery({
    queryKey: ['gateway-diagnostics', deviceId],
    queryFn: async (): Promise<DiagnosticSession[]> => {
      if (!deviceId) return [];
      const { data, error } = await supabase
        .from('gateway_diagnostics')
        .select('*')
        .eq('device_id', deviceId)
        .order('reported_at', { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);

      const reports = (data ?? []) as GatewayDiagnosticReport[];
      const bySession = new Map<string, GatewayDiagnosticReport[]>();
      for (const r of reports) {
        const arr = bySession.get(r.session_id) ?? [];
        arr.push(r);
        bySession.set(r.session_id, arr);
      }

      return Array.from(bySession.entries()).map(([session_id, sessionReports]) => {
        const sorted = sessionReports.sort(
          (a, b) => new Date(a.reported_at).getTime() - new Date(b.reported_at).getTime()
        );
        return {
          session_id,
          reports: sorted,
          start: sorted[0].reported_at,
          end: sorted[sorted.length - 1].reported_at,
        };
      });
    },
    enabled: Boolean(deviceId),
    staleTime: 30_000,
  });

  return {
    sessions: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
